// /frontend/src/App.js
import React, { useEffect, useRef, Suspense, useState } from "react";
import {
  Routes,
  Route,
  useNavigate,
  useLocation,
  HashRouter,
  useInRouterContext,
  Link,
  Navigate,
} from "react-router-dom";
import { applyTheme, getSavedTheme } from "./utils/themeToggle";
import "./assets/styles/theme.css";

import { SearchProvider } from "./context/SearchContext";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LoaderProvider, useLoader } from "./context/GlobalLoaderContext";
import Loader from "./components/LoaderComponent";
import Header from "./components/Header";
import GlobalQuoteModal from "./components/GlobalQuoteModal";
import CompareView from "./pages/CompareView";

// --- Mobile feature flag ---
const ENABLE_MOBILE = process.env.REACT_APP_ENABLE_MOBILE === "1";
const MobileRoutes = () => null;

// ── ErrorBoundary ────────────────────────────────────────────────────────────
class InlineErrorBoundary extends React.Component {
  constructor(p) {
    super(p);
    this.state = { err: null, info: null };
  }
  static getDerivedStateFromError(err) {
    return { err };
  }
  componentDidCatch(error, info) {
    console.error("[App Error]", error, info);
    this.setState({ info });
  }
  render() {
    const { err, info } = this.state;
    if (!err) return this.props.children;
    return (
      <div style={{ padding: 16, fontFamily: "sans-serif" }}>
        <h2>💥 App Error</h2>
        <pre style={pre}>{String(err?.message || err)}</pre>
        {err?.stack ? (
          <>
            <h3>Stack</h3>
            <pre style={pre}>{err.stack}</pre>
          </>
        ) : null}
        {info?.componentStack ? (
          <>
            <h3>Component Stack</h3>
            <pre style={pre}>{info.componentStack}</pre>
          </>
        ) : null}
      </div>
    );
  }
}
const pre = {
  whiteSpace: "pre-wrap",
  background: "#f7f7f7",
  padding: 12,
  borderRadius: 6,
};

// ── Router Wrapper (กันซ้อน) ────────────────────────────────────────────────
function OptionalRouter({ children }) {
  const inCtx = useInRouterContext();
  return inCtx ? children : <HashRouter>{children}</HashRouter>;
}

// ── หน้า baseline สำหรับเทสต์ ─────────────────────────────────────────────
function BaselineHome() {
  return (
    <div
      style={{
        padding: 16,
        fontFamily:
          "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
      }}
    >
      ✅ BASELINE OK — {new Date().toISOString()}
      <div style={{ marginTop: 10, color: "#444" }}>
        ไปหน้า: <Link to="/">/</Link> • <Link to="/gallery">/gallery</Link> •{" "}
        <Link to="/login">/login</Link> •{" "}
        <Link to="/public/desktop-survey">/public/desktop-survey</Link> •{" "}
        <Link to="/vtravel">/vtravel</Link> • <Link to="/serp">/serp</Link> •{" "}
        <Link to="/serp/trends">/serp/trends</Link> •{" "}
        <Link to="/new-flow">/new-flow</Link> •{" "}
        <Link to="/photo-assist">/photo-assist</Link>
      </div>
    </div>
  );
}

// ── lazy imports ─────────────────────────────────────────────────────────────
const CompanyHome = React.lazy(() => import("./pages/CompanyHome"));
const PrbPage = React.lazy(() => import("./pages/PrbPage")); // ✅ หน้า พ.ร.บ. แยก
const AdminLogin = React.lazy(() => import("./pages/AdminLogin"));
const Login = React.lazy(() => import("./pages/Login"));
const Register = React.lazy(() => import("./pages/Register"));
const ForgotPassword = React.lazy(() => import("./pages/ForgotPassword"));
const PolicyMockupForm = React.lazy(() => import("./pages/PolicyMockupForm"));
const OCRDocument = React.lazy(() => import("./pages/OCRDocument"));
const CarBrandsPage = React.lazy(() => import("./pages/CarBrandsPage"));
const PackageGallery = React.lazy(() => import("./components/PackageGallery"));
const Profile = React.lazy(() => import("./pages/Profile"));
const EditProfile = React.lazy(() => import("./pages/EditProfile"));
const PackageList = React.lazy(() => import("./components/PackageList"));
const MultiStepSearch = React.lazy(() => import("./components/MultiStepSearch"));
const PackageDetail = React.lazy(() => import("./components/PackageDetails"));
const PrepareInsuranceInfo = React.lazy(
  () => import("./pages/PrepareInsuranceInfo")
);
const PolicyDocument = React.lazy(() => import("./pages/PolicyDocument"));
const ReportClaim = React.lazy(() => import("./pages/ReportClaim"));
const ReviewInsurance = React.lazy(() => import("./pages/ReviewInsurance"));
const SearchResults = React.lazy(() => import("./components/SearchResults"));
const OrderStatus = React.lazy(() => import("./components/OrderStatus"));
const AdminDashboard = React.lazy(
  () => import("./pages/admin/AdminDashboard")
);
const AdminWallet = React.lazy(
  () => import("./pages/admin/components/AdminWallet")
);
const AdminProfile = React.lazy(() => import("./pages/AdminProfile"));
const UploadedDocumentsPage = React.lazy(
  () => import("./pages/UploadedDocumentsPage")
);
// Risk Hub Admin
const RiskHubDashboard = React.lazy(
  () => import("./pages/admin/risk/RiskHubDashboard")
);

const PrbKnowledgeCards = React.lazy(
  () => import("./pages/PrbKnowledgeCards")
);



const Chatbot = React.lazy(() => import("./components/Chatbot"));
const AdminDefaults = React.lazy(() => import("./pages/AdminDefaults"));
const PublicPresetEstimator = React.lazy(
  () => import("./pages/PublicPresetEstimator")
);
const CargoWizard = React.lazy(() => import("./pages/CargoWizard"));
const RenewalsAdmin = React.lazy(() => import("./components/RenewalsAdmin"));
const CompareCore = React.lazy(() => import("./pages/CompareCore"));
const CompareCoreTabsPage = React.lazy(
  () => import("./pages/CompareCoreTabsPage")
);
const VTravelWizard = React.lazy(() => import("./pages/VTravelWizard"));
const ProductGallery = React.lazy(() => import("./pages/ProductGallery"));
const MyPage = React.lazy(() => import("./pages/MyPage"));
const QuizGame = React.lazy(() => import("./pages/QuizGame"));
const TaladrodMarket = React.lazy(() => import("./pages/TaladrodMarket"));
const FNOLAssist = React.lazy(() => import("./pages/FNOLAssist"));
const QuotesForm = React.lazy(() => import("./pages/QuotesForm"));
const PublicQuote = React.lazy(() => import("./pages/PublicQuote"));
const ForumHome = React.lazy(() => import("./pages/forum/ForumHome"));
const NewThreadForm = React.lazy(
  () => import("./pages/forum/NewThreadForm")
);
const ThreadDetail = React.lazy(
  () => import("./pages/forum/ThreadDetail")
);
const AdminCards = React.lazy(() => import("./pages/admin/cards/AdminCards"));
const GeoRiskForm = React.lazy(() => import("./components/GeoRiskForm"));
const PublicDesktopSurvey = React.lazy(
  () => import("./pages/PublicDesktopSurvey")
);
const SerpLab = React.lazy(() => import("./pages/SerpLab"));
const NewFlowPage = React.lazy(() => import("./pages/NewFlowPage"));
const HighlightsPage = React.lazy(() => import("./pages/HighlightsPage"));
const RiskConsole = React.lazy(() => import("./pages/RiskConsole"));
const PhotoAssistPage = React.lazy(() => import("./pages/PhotoAssistPage"));
const CarInsuranceCompare = React.lazy(
  () => import("./pages/CarInsuranceCompare")
);

const CarModelSeoPage = React.lazy(
  () => import("./pages/CarModelSeoPage")
);

const VoicePage = React.lazy(() => import("./pages/VoicePage"));
const RidgwayPrice = React.lazy(() => import("./components/RidgwayPrice"));
const InsurancePanel = React.lazy(
  () => import("./components/InsurancePanel")
);
const MotorPolicyInteractive = React.lazy(
  () => import("./pages/MotorPolicyInteractive")
);
const FloodEscapeGame = React.lazy(
  () => import("./components/EscapeGame/FloodEscapeGame")
);
const FloodFireLab = React.lazy(
  () => import("./components/EscapeGame/FloodFireLab")
);

const CarDamageLab = React.lazy(() => import("./pages/CarDamageLab"));

// Risk Hub + Tools
const RiskCenter = React.lazy(() => import("./pages/risk/RiskCenter"));
const RiskSurveyChecklistGenerator = React.lazy(
  () => import("./pages/risk/RiskSurveyChecklistGenerator")
);
const ElectricalSafetySurvey = React.lazy(
  () => import("./pages/risk/ElectricalSafetySurvey")
);
const FireProtectionChecklist = React.lazy(
  () => import("./pages/risk/FireProtectionChecklist")
);
const ChemicalStorageChecklist = React.lazy(
  () => import("./pages/risk/ChemicalStorageChecklist")
);
const HotWorkPermitChecklist = React.lazy(
  () => import("./pages/risk/HotWorkPermitChecklist")
);
const BusinessInterruptionWorksheet = React.lazy(
  () => import("./pages/risk/BusinessInterruptionWorksheet")
);
const FloodExposureAssessment = React.lazy(
  () => import("./pages/risk/FloodExposureAssessment")
);
const FacilityRiskSummary = React.lazy(
  () => import("./pages/risk/FacilityRiskSummary")
);
const RiskMaturityScore = React.lazy(
  () => import("./pages/risk/RiskMaturityScore")
);

const RiskConsolePro = React.lazy(() => import("./pages/risk/RiskConsolePro"));


const PolicyMotorTableExplainer = React.lazy(
  () => import("./components/PolicyMotorTableExplainer")
);
const PolicyMotorSpecialTableExplainer = React.lazy(
  () => import("./components/PolicyMotorSpecialTableExplainer")
);
const PolicyMotorLongTermTableExplainer = React.lazy(
  () => import("./components/PolicyMotorLongTermTableExplainer")
);
const PolicyEndorsementTableExplainer = React.lazy(
  () => import("./components/PolicyEndorsementTableExplainer")
);
const RiskImprovementFormula = React.lazy(() =>
  import("./pages/risk/RiskImprovementFormula")
);
const SurveyChecklistForm = React.lazy(
  () => import("./components/Survey/SurveyChecklistForm")
);
const SurveyChecklistPrint = React.lazy(
  () => import("./components/Survey/SurveyChecklistPrint")
);

const Estimator = React.lazy(() => import("./pages/Estimator"));


// Guards
import PrivateRoute from "./components/PrivateRoute";
import AdminRoute from "./components/AdminRoute";

// 404
const NotFound = () => (
  <div style={{ textAlign: "center", padding: 50 }}>
    <h1>404 - Page Not Found</h1>
    <Link to="/" style={{ textDecoration: "none", color: "blue" }}>
      กลับไปหน้าแรก
    </Link>
  </div>
);

/**
 * Public routes
 */
const PUBLIC_EXACT = new Set([
  "/",
  "/login",
  "/register",
  "/forgot-password",
  "/admin/login",
  "/car-brands",
  "/mockup-policy",
  "/ocr",
  "/gallery",
  "/baseline",
  "/public/preset-estimator",
  "/embed-preset",
  "/chat",
  "/cargo-wizard",
  "/compare/core",
  "/compare/core/basic",
  "/embed-vtravel",
  "/packages",
  "/compare",
  "/quiz",
  "/taladrod",
  "/fnol",
  "/forum",
  "/georisk",
  "/public/desktop-survey",
  "/embed-desktop-survey",
  "/m",
  "/my-page",
  "/serp",
  "/serp/quick",
  "/serp/trends",
  "/new-flow",
  "/search-results",
  "/highlights",
  "/prb", // ✅ PRB แยกหน้า

  // Risk Hub + tools (ทั้งหมดเป็น public ให้ Risk Engineer เข้าใช้ได้เลย)
  "/risk",               // Industrial Risk Console
  "/risk/hub",           // Risk Engineering Hub
  "/risk/checklist-generator",
  "/risk/electrical-safety",
  "/risk/fire-protection",
  "/risk/chemical-storage",
  "/risk/hot-work",
  "/risk/bi-worksheet",
  "/risk/flood-exposure",
  "/risk/facility-summary",
  "/risk/risk-maturity",
  "/risk/improvement-formula",
  "/risk/console-pro",

  "/photo-assist",
  "/debug/voice",
  "/ridgway",
  "/embed/ridgway",
  "/insurance-panel",
  "/policy-motor-table",
  "/policy-motor-table-1-2",
  "/policy-motor-table-1-3",
  "/policy-endorsement-table-2-1",
  "/policy-interactive",
  "/flood-escape",
  "/flood-fire-lab",
  "/damage-lab",
  "/prb/cards",
  "/risk/survey-checklist-form",
  "/risk/survey-checklist-form/print",
  "/aurora",
  "/embed/aurora",

]);
const PUBLIC_PREFIXES = ["/package/", "/p/", "/car-insurance/"];

function isPublicPath(pathname) {
  if (PUBLIC_EXACT.has(pathname)) return true;
  return PUBLIC_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

const AppContent = () => {
  const { isLoading } = useLoader();
  const { isAuthInitializing, authToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const hasRedirected = useRef(false);

  // Global Quote Modal
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const openQuoteModal = (pkg) => {
    setSelectedPackage(pkg || null);
    setQuoteOpen(true);
  };

  // theme
  useEffect(() => {
    applyTheme(getSavedTheme());
  }, []);

  // auth redirect
  useEffect(() => {
    const publicOk = isPublicPath(location.pathname);
    const isAdminPath = location.pathname.startsWith("/admin");
    if (
      !isAuthInitializing &&
      !authToken &&
      !hasRedirected.current &&
      !publicOk &&
      !isAdminPath
    ) {
      hasRedirected.current = true;
      navigate("/login", { replace: true });
    }
  }, [isAuthInitializing, authToken, location.pathname, navigate]);

  // robust embed detector
  const isEmbed = React.useMemo(() => {
    try {
      if (window.self !== window.top) return true;

      const href = window.location.href || "";
      const partAfterHash = href.includes("#") ? href.split("#")[1] : "";
      const qsFromHash = partAfterHash.includes("?")
        ? partAfterHash.split("?")[1]
        : "";
      const qsFromSearch = (location.search || "").replace(/^\?/, "");
      const qs = qsFromHash || qsFromSearch;

      const p = new URLSearchParams(qs);
      const v = (p.get("embed") || "").toLowerCase().trim();
      return v === "1" || v === "true" || v === "yes";
    } catch {
      return false;
    }
  }, [location.search, location.hash]);

  useEffect(() => {
    const el = document.documentElement;
    if (isEmbed) el.classList.add("is-embed");
    else el.classList.remove("is-embed");
    return () => el.classList.remove("is-embed");
  }, [isEmbed]);

  if (isAuthInitializing || isLoading) {
    return (
      <p style={{ textAlign: "center", padding: 50 }}>
        🔄 กำลังโหลดข้อมูล...
      </p>
    );
  }

  return (
    <>
      {isLoading && <Loader />}
      {!isEmbed && <Header />}
  
      <Suspense fallback={<div style={{ padding: 16 }}>🔄 กำลังโหลดหน้า…</div>}>
        <Routes>
          {/* Home */}
          <Route path="/" element={<CompanyHome />} />
          <Route path="/baseline" element={<BaselineHome />} />
          {/* ✅ PRB หน้าแยก */}
          <Route path="/prb" element={<PrbPage />} />
  
          {/* redirects */}
          <Route path="/home" element={<Navigate to="/" replace />} />
          <Route path="/company" element={<Navigate to="/" replace />} />
  
          {/* public */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/car-brands" element={<CarBrandsPage />} />
          <Route path="/mockup-policy" element={<PolicyMockupForm />} />
          <Route path="/ocr" element={<OCRDocument />} />
          <Route path="/photo-assist" element={<PhotoAssistPage />} />
          <Route path="/debug/voice" element={<VoicePage />} />
  
          <Route
            path="/insurance-panel"
            element={
              <InsurancePanel
                initialFilters={{
                  brand: "TOYOTA",
                  model: "YARIS",
                  year: 2020,
                  insurance_type: "2+",
                }}
              />
            }
          />
  
          {/* 👇 เพิ่ม route หน้ากรมธรรม์รถยนต์ 1.1 */}
          <Route path="/policy-motor-table" element={<PolicyMotorTableExplainer />} />
          <Route path="/policy-motor-table-1-2" element={<PolicyMotorSpecialTableExplainer />} />
          {/* 🧾 ชุดที่ 2 – ตารางสลักหลัง 2.1 */}
          <Route path="/policy-endorsement-table-2-1" element={<PolicyEndorsementTableExplainer />} />
          <Route path="/policy-motor-table-1-3" element={<PolicyMotorLongTermTableExplainer />} />
          <Route path="/policy-interactive" element={<MotorPolicyInteractive />} />
  
          {/* gallery */}
          <Route
            path="/gallery"
            element={<PackageGallery onRequestQuote={openQuoteModal} />}
          />
          <Route path="/embed-gallery" element={<ProductGallery />} />
          <Route path="/fnol" element={<FNOLAssist />} />
  
          {/* 👇 Ridgway-style Motor Package Plate */}
          <Route path="/ridgway" element={<RidgwayPrice onSelectPackage={openQuoteModal} />} />
          <Route path="/embed/ridgway" element={<Navigate to="/ridgway?embed=1" replace />} />
  
          {/* forum */}
          <Route path="/forum" element={<ForumHome />} />
          <Route path="/forum/new" element={<NewThreadForm />} />
          <Route path="/forum/threads/:threadId" element={<ThreadDetail />} />
  
          <Route path="/georisk" element={<GeoRiskForm />} />
          <Route path="/flood-escape" element={<FloodEscapeGame />} />
          <Route path="/flood-fire-lab" element={<FloodFireLab />} />
  
          {/* 👇 หน้า Car Damage Lab ใหม่ */}
          <Route path="/damage-lab" element={<CarDamageLab />} />
  
          {/* public survey */}
          <Route path="/public/desktop-survey" element={<PublicDesktopSurvey />} />
          <Route path="/embed-desktop-survey" element={<PublicDesktopSurvey />} />
  
          {/* ✅ PRB Knowledge Cards (ใหม่) */}
          <Route path="/prb/cards" element={<PrbKnowledgeCards />} />
  
          <Route path="/chat" element={<Chatbot floating={false} />} />
          <Route path="/cargo-wizard" element={<CargoWizard />} />
          <Route path="/vtravel" element={<VTravelWizard />} />
          <Route path="/embed-vtravel" element={<VTravelWizard />} />
  
          {/* packages page */}
          <Route path="/packages" element={<ProductGallery />} />
  
          {/* highlights */}
          <Route path="/highlights" element={<HighlightsPage />} />
          <Route path="/embed/highlights" element={<Navigate to="/highlights?embed=1" replace />} />
  
          {/* results */}
          <Route path="/search-results" element={<SearchResults />} />
  
          <Route path="/compare" element={<CarInsuranceCompare />} />
          <Route path="/compare/legacy" element={<CompareView />} /> {/* optional เก็บหน้าเก่า */}
  
          <Route path="/taladrod" element={<TaladrodMarket />} />
          <Route path="/p/:publicId" element={<PublicQuote />} />
  
          {/* 🔹 เพิ่ม Route หน้าประกันตามรุ่นรถ (SEO Page) */}
          <Route path="/car-insurance/:brand/:model" element={<CarModelSeoPage />} />
          <Route path="/embed/aurora" element={<Navigate to="/aurora?embed=1" replace />} />


  
          {/* estimators */}
          {/* estimators */}
          <Route path="/public/preset-estimator" element={<PublicPresetEstimator />} />
          <Route path="/embed-preset" element={<PublicPresetEstimator />} />

          {/* ✅ AURORA Cost Engine */}
          <Route path="/aurora" element={<Estimator />} />
          <Route path="/embed/aurora" element={<Navigate to="/aurora?embed=1" replace />} />

  
          <Route path="/quiz" element={<QuizGame />} />
          <Route path="/package/:id" element={<PackageDetail />} />
          <Route path="/my-page" element={<MyPage />} />
  
          {/* SERP */}
          <Route path="/serp" element={<SerpLab />} />
          <Route path="/serp/quick" element={<SerpLab />} />
          <Route path="/serp/trends" element={<SerpLab />} />
  
          {/* Industrial Risk Hub + Tools */}
          <Route path="/risk" element={<RiskConsole />} /> {/* Industrial Risk เดิม */}
          <Route path="/risk/hub" element={<RiskCenter />} /> {/* Risk Engineering Hub */}
          <Route path="/risk/improvement-formula" element={<RiskImprovementFormula />} />
          <Route path="/risk/checklist-generator" element={<RiskSurveyChecklistGenerator />} />
          <Route path="/risk/electrical-safety" element={<ElectricalSafetySurvey />} />
          <Route path="/risk/fire-protection" element={<FireProtectionChecklist />} />
          <Route path="/risk/chemical-storage" element={<ChemicalStorageChecklist />} />
          <Route path="/risk/hot-work" element={<HotWorkPermitChecklist />} />
          <Route path="/risk/bi-worksheet" element={<BusinessInterruptionWorksheet />} />
          <Route path="/risk/flood-exposure" element={<FloodExposureAssessment />} />
          <Route path="/risk/facility-summary" element={<FacilityRiskSummary />} />
          <Route path="/risk/risk-maturity" element={<RiskMaturityScore />} />
          <Route path="/risk/console-pro" element={<RiskConsolePro />} />
          <Route path="/risk/console" element={<RiskConsole />} /> {/* alias */}
  
          {/* ✅ Survey Checklist Form + Print */}
          <Route path="/risk/survey-checklist-form" element={<SurveyChecklistForm />} />
          <Route path="/risk/survey-checklist-form/print" element={<SurveyChecklistPrint />} />
  
          {/* New Flow */}
          <Route path="/new-flow" element={<NewFlowPage />} />
  
          {/* mobile mount */}
          {ENABLE_MOBILE ? <Route path="/m/*" element={<MobileRoutes />} /> : null}
  
          {/* Compare */}
          <Route path="/compare/core" element={<CompareCoreTabsPage />} />
          <Route path="/compare/core/basic" element={<CompareCore />} />
  
          {/* private (user) */}
          <Route
            path="/profile"
            element={
              <PrivateRoute>
                <Profile />
              </PrivateRoute>
            }
          />
          <Route
            path="/edit-profile"
            element={
              <PrivateRoute>
                <EditProfile />
              </PrivateRoute>
            }
          />
          <Route
            path="/quotes/new"
            element={
              <PrivateRoute>
                <QuotesForm />
              </PrivateRoute>
            }
          />
  
          {/* admin protected */}
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/dashboard"
            element={
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/profile"
            element={
              <AdminRoute>
                <AdminProfile />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/defaults"
            element={
              <AdminRoute>
                <AdminDefaults />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/wallet"
            element={
              <AdminRoute>
                <AdminWallet />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/renewals"
            element={
              <AdminRoute>
                <RenewalsAdmin />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/cards"
            element={
              <AdminRoute>
                <AdminCards />
              </AdminRoute>
            }
          />
          <Route
            path="/admin/risk-hub"
            element={
              <AdminRoute>
                <RiskHubDashboard />
              </AdminRoute>
            }
          />
  
          {/* user protected */}
          <Route
            path="/packages/manage"
            element={
              <PrivateRoute>
                <PackageList />
              </PrivateRoute>
            }
          />
          <Route
            path="/compare/wizard"
            element={
              <PrivateRoute>
                <MultiStepSearch />
              </PrivateRoute>
            }
          />
          <Route
            path="/prepare-insurance-info"
            element={
              <PrivateRoute>
                <PrepareInsuranceInfo />
              </PrivateRoute>
            }
          />
          <Route
            path="/policy-document"
            element={
              <PrivateRoute>
                <PolicyDocument />
              </PrivateRoute>
            }
          />
          <Route
            path="/report-claim"
            element={
              <PrivateRoute>
                <ReportClaim />
              </PrivateRoute>
            }
          />
          <Route
            path="/review-insurance"
            element={
              <PrivateRoute>
                <ReviewInsurance />
              </PrivateRoute>
            }
          />
          <Route
            path="/order/:orderId"
            element={
              <PrivateRoute>
                <OrderStatus />
              </PrivateRoute>
            }
          />
          <Route
            path="/uploaded-documents"
            element={
              <PrivateRoute>
                <UploadedDocumentsPage />
              </PrivateRoute>
            }
          />
  
          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
  
        {/* Quote Modal */}
        <GlobalQuoteModal
          open={quoteOpen}
          onClose={() => setQuoteOpen(false)}
          packageInfo={selectedPackage}
        />
      </Suspense>
    </>
  );
  
};

// ── default export ────────────────────────────────────────────────────────────
export default function App() {
  return (
    <OptionalRouter>
      <AuthProvider>
        <LoaderProvider>
          <InlineErrorBoundary>
            <SearchProvider>
              <AppContent />
            </SearchProvider>
          </InlineErrorBoundary>
        </LoaderProvider>
      </AuthProvider>
    </OptionalRouter>
  );
}
